import clsx from "clsx";
import { useState } from "react";
import PosingDeb from "./PosingDeb";
import Typography from "./typography/Typography";
import Link from "next/link";

type ErrorSeverity = "fatal" | "warning" | "info";
type RecoveryMode = "callback" | "hardlink";

type ErrorPaneProps = {
  severity: ErrorSeverity;
  className?: string;
  title?: string;
  message: string;
  smallPrint?: string;
  onClickRetry?: () => void;
  recoveryMode: RecoveryMode;
  recoveryButtonText?: string;
};

const ErrorPane = ({ className, severity, message, title, smallPrint, recoveryMode, onClickRetry, recoveryButtonText }: ErrorPaneProps) => {
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  const getDefaultTitle = (severity: ErrorSeverity) => {
    switch (severity) {
      case "fatal":
        return "There's a problem";
      case "warning":
        return "Something's up";
      default:
      case "info":
        return "Just a heads up";
    }
  };

  title = title || getDefaultTitle(severity);

  return (
    <div
      className={clsx(
        "w-full h-full pt-10 bg-base-100 text-base-content flex flex-col justify-between gap-4",
        className,
      )}
    >
      <PosingDeb debPose={severity === "fatal" ? "sad" : severity === "warning" ? "oops" : "wave"} debSize="large" />
      <Typography className="text-h1">{title}</Typography>
      <Typography className="text-body">{message}</Typography>
      {smallPrint && (
        <div className="mt-2">
          {!isDetailsExpanded ? (
            <button
              onClick={() => setIsDetailsExpanded(true)}
              className="p-0 btn btn-secondary btn-ghost btn-sm"
            >
              Show more details
            </button>
          ) : (
            <div
              className={`p-2 w-full max-h-[300px] border-[1px] border-base-content rounded-md flex 
              flex-col items-center justify-between overflow-y-auto`}
            >
              <div className="text-micro grow">{smallPrint}</div>
              <button
                onClick={() => setIsDetailsExpanded(false)}
                className="mt-auto btn btn-secondary btn-ghost btn-sm"
              >
                Hide
              </button>
            </div>
          )}
        </div>
      )}
      {/* BOTTOM SECTION */}
      <div className="mt-auto mb-4">
        {recoveryMode === "hardlink" && (
          <div className="w-full btn btn-sm btn-secondary rounded-xl rounded-bl-none mt-4">
            <Link href="/">{recoveryButtonText || "Restart App"}</Link>
          </div>
        )}
        {recoveryMode === "callback" && onClickRetry && (
          <button
            onClick={onClickRetry}
            className="w-full btn btn-sm btn-secondary rounded-xl rounded-bl-none mt-4"
          >
            {recoveryButtonText || "Try reloading"}
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorPane;
